import { Box, TextField, Typography } from '@mui/material';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import Button from '@src/App/Components/Buttons/Button/Button';
import Dialog from '@src/App/Components/Dialogs/Dialog/Dialog';
import type { AppState } from '@store/store';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import type { ITemplate } from './types';

interface ImportTemplateDialogProps {
    id: string;
    currentTemplate: ITemplate;
    resolve?: (template?: ITemplate) => void;
}

export const ImportTemplateDialog: React.FC<ImportTemplateDialogProps> = ({ id, currentTemplate, resolve }) => {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeTalk;

    const [jsonString, setJsonString] = useState('');
    const [error, setError] = useState<string | null>(null);

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setJsonString(e.target.value);
        setError(null);
    };

    const handleImport = () => {
        try {
            if (!jsonString.trim()) {
                setError(langStrings.emptyImport);
                return;
            }

            const importedTemplate = JSON.parse(jsonString);

            // Validiere das importierte Template (minimale Prüfung)
            if (!importedTemplate.pages || !Array.isArray(importedTemplate.pages)) {
                setError(langStrings.invalidTemplate);
                return;
            }

            // Behalte die ursprüngliche ID und Version bei
            const mergedTemplate = {
                ...importedTemplate,
                id: currentTemplate.id,
                templateVersion: currentTemplate.templateVersion,
                isActive: currentTemplate.isActive,
            };

            resolve && resolve(mergedTemplate);
        } catch (error) {
            console.error('Error parsing JSON:', error);
            setError(langStrings.invalidJson);
        }
    };

    return (
        <Dialog
            id={id}
            title={langStrings.importTemplate}
            footer={
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, width: '100%' }}>
                    <Button text={langStrings.cancel} onClick={() => resolve && resolve()} />
                    <Button text={langStrings.import} color="primary" onClick={handleImport} />
                </Box>
            }
            onClose={() => resolve && resolve()}
        >
            <Box sx={{ width: '100%' }}>
                <Typography variant="body1" gutterBottom>
                    {langStrings.importTemplateDescription}
                </Typography>
                <TextField
                    multiline
                    fullWidth
                    rows={15}
                    value={jsonString}
                    onChange={handleTextChange}
                    variant="outlined"
                    error={!!error}
                    helperText={error}
                    sx={{ fontFamily: 'monospace', mt: 2 }}
                />
            </Box>
        </Dialog>
    );
};

export default ImportTemplateDialog;
